import { customElement, state } from 'lit/decorators.js';
import {
  fireQuickErrorToast,
  fireQuickInformationToast,
  fireQuickSuccessToast,
  fireQuickWarningToast
} from '../../../webmodule-common/other/toast-away';
import { FabricationJobContainerManager, getConfiguredKerf } from '../data/fabrication-job-container';
import { goStaticURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { html, nothing, TemplateResult } from 'lit';
import { LangOptimizationPanel } from '../../../localization/scripts/fabrication-jobs/views/optimization-panel-lang';
import { OptimizationResult, OptimizationState } from '../../api/fabrication-job-api';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import { virtualPathToUrl } from '../../quotes/data/v6/helper-functions';

export interface OptimizationPanelOptions {
  containerManager: FabricationJobContainerManager;
}

// Bottom-of-page summary panel for the Job Items tab. Reuses the same
// quote-price-summary skeleton as the franchisee Quote Items view (left
// terms column kept empty for layout, right column carries everything) so
// the responsive breakpoints, the white-background readonly rows and the
// bottom-right action-icon slot all match the quotes screen verbatim.
// Kerf, kerf angle, cutplans, total bars, total bar length, and waste %
// each render as a label/value line item; the primary action lives at the
// bottom of the column, left-aligned so the same button position is used
// for Optimize, Lock In and Retry.
@customElement('wm-optimization-panel')
export class OptimizationPanel extends ViewBase {
  private options: OptimizationPanelOptions;
  @state() private busy = false;
  @state() private busyAction: 'optimize' | 'lockIn' | 'cancel' | null = null;

  // Stays true between a successful Optimize click and the first observed
  // state transition out of Idle. Closes the visual gap where busy=false has
  // already fired but the SSE / refetch hasn't surfaced state=InProgress yet,
  // so the button can't be double-clicked into a second Start.
  @state() private pendingStart = false;

  // Why? Kerf values render straight from franchisee properties (not the
  // job container), so a save in a sibling tab leaves the displayed value
  // stale. PageResumeHandler refreshes userDataStore on visibility change;
  // this subscription is the trigger that turns that refresh into a re-render.
  private _unsubscribeFranchiseeChange: (() => void) | null = null;

  constructor(options: OptimizationPanelOptions) {
    super();
    this.options = options;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Guard against re-attach without a paired disconnectedCallback - Lit
    // can move elements across the DOM tree without tearing them down.
    if (this._unsubscribeFranchiseeChange) return;
    this._unsubscribeFranchiseeChange = userDataStore.onFranchiseeChanged(() => this.refresh());
  }

  override disconnectedCallback(): void {
    this._unsubscribeFranchiseeChange?.();
    this._unsubscribeFranchiseeChange = null;
    super.disconnectedCallback();
  }

  private get containerManager(): FabricationJobContainerManager {
    return this.options.containerManager;
  }

  // SSE-driven refresh: re-render so the panel reflects the new optimization
  // state coming from the backend. Any observed state other than Idle means
  // the kickoff has been picked up, so the pendingStart guard can be released.
  public refresh(): void {
    const optState = this.containerManager.optimizationResult?.state;
    if (this.pendingStart && optState !== undefined && optState !== OptimizationState.Idle) {
      this.pendingStart = false;
    }
    this.requestUpdate();
  }

  protected override template(): TemplateResult {
    return html`
      <form class="py-3 px-0 quote-price-summary form-two-col">
        <div class="row">
          <div class="quote-items-terms"></div>
          <div class="quote-items-summary">
            <div class="quote-items-summary-wrapper">
              <div class="quote-items-summary-data">${this.rightColumnTemplate()}</div>
            </div>
          </div>
        </div>
      </form>
    `;
  }

  private rightColumnTemplate(): TemplateResult {
    const cm = this.containerManager;
    const result = cm.optimizationResult;
    const optState = result?.state ?? OptimizationState.Idle;
    const isDraft = cm.isDraft;
    const lockedIn = !isDraft && optState === OptimizationState.Completed;

    const props = userDataStore.getFranchisee().properties;
    const kerfDisplay = props['optimizer-kerf-display'] || '-';
    const kerfAngleDisplay = props['optimizer-kerf-angle-display'] || '-';

    const errorBlock =
      optState === OptimizationState.Failed && result?.errorMessage
        ? html`<p class="text-danger small mt-1 mb-0 text-end">
            ${LangOptimizationPanel.errorDetailLabel(result.errorMessage)}
          </p>`
        : nothing;

    return html`
      ${this.kerfRow(kerfDisplay, kerfAngleDisplay)}
      ${this.alignedRow(this.summaryRow(LangOptimizationPanel.cutplansRowLabel(), this.cutplanCountDisplay(result)))}
      ${this.alignedRow(
        this.summaryRow(LangOptimizationPanel.totalBarCountRowLabel(), this.totalBarCountDisplay(result))
      )}
      ${this.alignedRow(
        this.summaryRow(LangOptimizationPanel.totalBarLengthRowLabel(), this.totalBarLengthDisplay(result))
      )}
      ${this.alignedRow(this.summaryRow(LangOptimizationPanel.wasteRowLabel(), this.wasteDisplay(result)))}
      ${errorBlock}
      <hr />
      ${this.actionFooter(optState, isDraft, lockedIn)}
    `;
  }

  // Idle / Failed: nothing to show. InProgress: "?" because the macro is
  // still running and the totals are not known yet. Completed: the value
  // captured at run-completion time.
  private summaryNumberDisplay(value: number | null | undefined, optState: OptimizationState): string {
    if (optState === OptimizationState.InProgress) return '?';
    if (value == null) return '-';
    return value.toString();
  }

  private cutplanCountDisplay(result: OptimizationResult | null): string {
    return this.summaryNumberDisplay(result?.cutplanCount, result?.state ?? OptimizationState.Idle);
  }

  private totalBarCountDisplay(result: OptimizationResult | null): string {
    return this.summaryNumberDisplay(result?.totalBarCount, result?.state ?? OptimizationState.Idle);
  }

  // Total bar length is formatted by V6 (DoubleToLengthStr) at run time and
  // round-tripped through the entity verbatim - no client-side unit
  // conversion. While the run is in progress the display string is null,
  // so show "?" until V6 fills it in on completion.
  private totalBarLengthDisplay(result: OptimizationResult | null): string {
    const optState = result?.state ?? OptimizationState.Idle;
    if (optState === OptimizationState.InProgress) return '?';
    return result?.totalBarLengthDisplay ?? '-';
  }

  // Waste = (total bar length - total extrusion length) / total bar length
  // * 100. Both totals are in V6 internal inches so the units cancel.
  private wasteDisplay(result: OptimizationResult | null): string {
    const optState = result?.state ?? OptimizationState.Idle;
    if (optState === OptimizationState.InProgress) return '?';
    if (result?.totalBarLength == null || result.totalExtrusionLength == null) return '-';
    if (result.totalBarLength <= 0) return '-';
    const waste = ((result.totalBarLength - result.totalExtrusionLength) / result.totalBarLength) * 100;
    return `${waste.toFixed(1)} %`;
  }

  // Wrap a non-kerf row so its input shares the same right edge as the
  // angled-kerf input above. The kerf row reserves space at the end for
  // the gear icon; mirroring that with an invisible icon-sized spacer keeps
  // every input flush on the right.
  private alignedRow(content: TemplateResult): TemplateResult {
    return html`
      <div class="d-flex align-items-center gap-2 mb-1">
        <div class="flex-grow-1">${content}</div>
        <span class="invisible" aria-hidden="true">${this.kerfSettingsLinkIcon()}</span>
      </div>
    `;
  }

  // Single-line row showing both kerf values side-by-side with the
  // settings-link icon at the end. Each label/value pair is rendered as
  // a webmodule-input with a slotted label so the label picks up the
  // exact same shadow-DOM typography as the "Optimization" status row
  // (font-weight, font-size, colour all defined inside the component).
  private kerfRow(kerfDisplay: string, kerfAngleDisplay: string): TemplateResult {
    const labeledInput = (label: string, value: string) => html`
      <webmodule-input
        class="webmodule-control-left-label flex-grow-1"
        size="small"
        type="text"
        readonly
        filled
        autocomplete="off"
        .value=${value}
      >
        <span slot="label">${label}</span>
      </webmodule-input>
    `;
    return html`
      <div class="d-flex align-items-center gap-2 mb-1">
        ${labeledInput(LangOptimizationPanel.kerfRowLabel(), kerfDisplay)}
        ${labeledInput(LangOptimizationPanel.kerfAngleRowLabel(), kerfAngleDisplay)} ${this.kerfSettingsLinkIcon()}
      </div>
    `;
  }

  // Render a label/value row using the same readonly, filled <webmodule-input>
  // that the quote-items-summary uses for money totals (white-background,
  // right-aligned values). This gives the optimizer panel rows the exact same
  // visual treatment as the quote totals block, without needing a DataTracker
  // for what is purely synthetic display data.
  private summaryRow(label: string, value: string): TemplateResult {
    return html`
      <webmodule-input
        class="webmodule-control-left-label"
        size="small"
        type="text"
        readonly
        filled
        autocomplete="off"
        .value=${value}
      >
        <span slot="label">${label}</span>
      </webmodule-input>
    `;
  }

  private kerfSettingsLinkIcon(): TemplateResult {
    return html`<webmodule-icon-button
      library="fa"
      name="fas-gear"
      label=${LangOptimizationPanel.editKerfTooltip()}
      title=${LangOptimizationPanel.editKerfTooltip()}
      @click=${this.onOpenKerfSettings}
    ></webmodule-icon-button>`;
  }

  // Action footer: Optimize + status badge on the left, View + Lock In on
  // the right. Buttons are enabled/disabled based on the current optimization
  // state so the layout stays stable across all states.
  private actionFooter(optState: OptimizationState, isDraft: boolean, lockedIn: boolean): TemplateResult {
    const cm = this.containerManager;
    const result = cm.optimizationResult;
    const hasItems = cm.items.length > 0;
    const pdfPath = optState === OptimizationState.Completed ? result?.cuttingPlanPdfPath : undefined;
    const optimizeDisabled =
      !isDraft || !hasItems || this.busy || this.pendingStart || optState === OptimizationState.InProgress;
    const lockInDisabled = !isDraft || this.busy || optState !== OptimizationState.Completed || lockedIn;
    const viewDisabled = !pdfPath;

    return html`<div class="d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <webmodule-button
          variant="primary"
          ?disabled=${optimizeDisabled}
          ?loading=${this.busy && this.busyAction === 'optimize'}
          @click=${this.onOptimize}
        >
          ${LangOptimizationPanel.optimizeButton()}
        </webmodule-button>
        ${optState === OptimizationState.InProgress
          ? html`
              <webmodule-spinner
                style="font-size: 1.5rem"
                title=${LangOptimizationPanel.stateInProgress()}
              ></webmodule-spinner>
              <webmodule-button
                variant="default"
                ?disabled=${this.busy}
                ?loading=${this.busy && this.busyAction === 'cancel'}
                @click=${this.onCancel}
              >
                ${LangOptimizationPanel.cancelButton()}
              </webmodule-button>
            `
          : nothing}
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <webmodule-button variant="default" ?disabled=${viewDisabled} @click=${() => this.onView(pdfPath)}>
          ${LangOptimizationPanel.viewButton()}
        </webmodule-button>
        <webmodule-button
          variant="primary"
          ?disabled=${lockInDisabled}
          ?loading=${this.busy && this.busyAction === 'lockIn'}
          @click=${this.onLockIn}
        >
          ${LangOptimizationPanel.lockInButton()}
        </webmodule-button>
      </div>
    </div>`;
  }

  private onView(pdfPath: string | null | undefined): void {
    if (!pdfPath) return;
    // virtualPathToUrl prepends the configured API host so the link works
    // when the UI dev server is on a different port than the dealer API.
    // The ?downloadName= query is an opt-in on FileController.Get that
    // overrides Content-Disposition's filename, so saving from the browser
    // PDF viewer suggests "Job <N> Cutplan Report <date>.pdf" instead of
    // the bare blob name "cutting-plan.pdf".
    const url =
      virtualPathToUrl(encodeURI(pdfPath)) + `?downloadName=${encodeURIComponent(this.cuttingPlanFileName())}`;
    window.open(url, '_blank', 'noopener');
  }

  private cuttingPlanFileName(): string {
    const job = this.containerManager.fabricationJob;
    const today = new Date();
    const yyyy = today.getFullYear().toString();
    const mm = (today.getMonth() + 1).toString().padStart(2, '0');
    const dd = today.getDate().toString().padStart(2, '0');
    return `Job ${job.jobNumber} Cutplan Report ${yyyy}-${mm}-${dd}.pdf`;
  }

  private onOpenKerfSettings = () => {
    // Why use goStaticURL instead of window.location? Because window.location bypasses the canLeave save-prompt chain, dropping unsaved per-unit overrides.
    goStaticURL('/settings#fabricator-settings');
  };

  private onOptimize = async () => {
    if (this.busy) return;
    // Why refresh before reading kerf? Kerf lives in franchisee properties,
    // not the job container. A save from another tab/device only reaches this
    // userDataStore via a server round-trip. Pulling here closes the window
    // where we'd otherwise post a stale value to the backend. Failure falls
    // back to the cached value - same as today's behaviour.
    try {
      await userDataStore.reLoadFranchisee();
    } catch {
      /* keep going with cached values */
    }
    if (getConfiguredKerf().straight <= 0) {
      fireQuickWarningToast(LangOptimizationPanel.kerfRequiredToast());
      return;
    }
    this.busy = true;
    this.busyAction = 'optimize';
    this.pendingStart = true;
    let kicked = false;
    try {
      const result = await this.containerManager.startOptimization();
      if (result?.optimizationResult) {
        kicked = true;
        fireQuickInformationToast(LangOptimizationPanel.optimizingToast());
      }
      // Why no else-branch toast? Because the kickoff endpoint is gated by OnlyAllowServerToServer; any legitimate failure throws and is caught below. A 200/null path is unreachable from the UI, so a toast here would only render a misleading "Optimization failed: " with no detail.
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      fireQuickErrorToast(LangOptimizationPanel.optimizationFailedToast(msg));
    } finally {
      if (!kicked) this.pendingStart = false;
      this.busy = false;
      this.busyAction = null;
      this.requestUpdate();
    }
  };

  private onLockIn = async () => {
    if (this.busy) return;
    this.busy = true;
    this.busyAction = 'lockIn';
    try {
      const result = await this.containerManager.lockInOptimization();
      if (result?.optimizationResult) {
        fireQuickSuccessToast(LangOptimizationPanel.lockedInToast());
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      fireQuickErrorToast(msg);
    } finally {
      this.busy = false;
      this.busyAction = null;
      this.requestUpdate();
    }
  };

  private onCancel = async () => {
    if (this.busy) return;
    this.busy = true;
    this.busyAction = 'cancel';
    try {
      const result = await this.containerManager.cancelOptimization();
      if (result?.optimizationResult) {
        fireQuickInformationToast(LangOptimizationPanel.cancelledToast());
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      fireQuickErrorToast(msg);
    } finally {
      this.busy = false;
      this.busyAction = null;
      this.requestUpdate();
    }
  };
}
